import { computed, signal } from '@preact/signals';
import {
  clampFinanceDuration,
  minDownPayment,
  roundCents,
  type ProviderName,
  type RateResponse,
  type RatedProductWire,
} from 'shared';
import { financeLimits } from '@/state/config.js';
import { carrySurchargeSelections, selectedSurchargeSum } from '@/lib/surcharges.js';
import { defaultDownPaymentInput, parseDownPayment } from '@/lib/finance.js';
import type { CartEntry, CartEntryPayment, Customer, Vehicle } from '@/lib/widget-types.js';

export type { CartEntry, Vehicle };

export const customer = signal<Customer | null>(null);
export const vehicle = signal<Vehicle | null>(null);
export const quoteId = signal<string | null>(null);
export const ratedProducts = signal<RatedProductWire[]>([]);
export const ratedAt = signal<number | null>(null);
export const unavailableProviders = signal<ProviderName[]>([]);
export const cart = signal<CartEntry[]>([]);

/** Undefined once a re-rate retires that quote item; consumers treat it as "unavailable". */
export function findRatedTier(quoteItemId: string): RatedProductWire | undefined {
  return ratedProducts.value.find((t) => t.quote_item_id === quoteItemId);
}
export const cartItemCount = computed(() => cart.value.length);

/** The cart is the source of truth once a product is in it, so the payment step writes here. */
export function setCartEntryPayment(index: number, payment: CartEntryPayment): void {
  const current = cart.value[index];
  if (current === undefined) return;
  const copy = [...cart.value];
  copy[index] = { ...current, payment };
  cart.value = copy;
}

export function removeCartEntry(index: number): void {
  cart.value = cart.value.filter((_, i) => i !== index);
}

/** Leaves `customer` and `vehicle` alone — outreach seeds them, marketplace clears them itself. */
export function resetRatingSession(): void {
  cart.value = [];
  quoteId.value = null;
  ratedProducts.value = [];
  ratedAt.value = null;
  unavailableProviders.value = [];
}

/** Signals are module-level, so a remount would otherwise inherit the previous mount's state. */
export function resetSession(): void {
  resetRatingSession();
  customer.value = null;
  vehicle.value = null;
}

/** 'clear' = new vehicle, nothing carries over. 'reconcile' = same vehicle, remap onto new ids. */
export type CartOnRate = 'clear' | 'reconcile';

/** Callers must state the cart policy — the two flows disagree, so it belongs at the call site. */
export function applyRateResponse(res: RateResponse, cartPolicy: CartOnRate): void {
  const nextCart =
    cartPolicy === 'reconcile'
      ? reconcileCart(cart.value, ratedProducts.value, res.rated_products)
      : [];

  quoteId.value = res.quote_id;
  ratedProducts.value = res.rated_products;
  ratedAt.value = Date.now();
  unavailableProviders.value = res.unavailable_providers;
  cart.value = nextCart;
}

/** A re-rate mints new `quote_item_id`s, so tiers are rematched on their identity fields. */
function reconcileCart(
  oldEntries: CartEntry[],
  oldRated: RatedProductWire[],
  newRated: RatedProductWire[],
): CartEntry[] {
  const out: CartEntry[] = [];
  for (const entry of oldEntries) {
    const oldTier = oldRated.find((t) => t.quote_item_id === entry.quoteItemId);
    if (oldTier === undefined) continue;
    const newTier = newRated.find(
      (t) =>
        t.product_id === oldTier.product_id &&
        t.term_months === oldTier.term_months &&
        t.coverage_name === oldTier.coverage_name &&
        t.deductible_amount === oldTier.deductible_amount,
    );
    if (newTier === undefined) continue;
    const surcharges = carrySurchargeSelections(entry.surcharges, newTier);
    out.push({
      quoteItemId: newTier.quote_item_id,
      surcharges,
      // A re-rate can move the price, so a down payment that cleared the floor before
      // may not now — carried forward unchecked, the cart would only fail at submit.
      payment: reclampPayment(entry.payment, newTier, surcharges),
    });
  }
  return out;
}

/**
 * Refits a payment choice when the tier moves under it — a term switch, or a re-rate that
 * repriced the plan. Clamps the duration, drops to pay-in-full when nothing is financeable,
 * and raises a down payment that has fallen under the new minimum.
 */
export function reclampPayment(
  payment: CartEntryPayment,
  tier: RatedProductWire,
  surcharges: CartEntry['surcharges'],
): CartEntryPayment {
  if (payment.type !== 'finance') return payment;

  const limits = financeLimits.value;
  const durationMonths = clampFinanceDuration(tier.term_months, payment.durationMonths, limits);
  if (durationMonths === null) return { type: 'pay_in_full' };

  // Rounded per component, like every other wholesale basis, so this floor is the one
  // `CartLine` will validate against.
  const wholesale =
    roundCents(tier.sale_price_finance) + roundCents(selectedSurchargeSum(tier, surcharges));
  const entered = parseDownPayment(payment.downPaymentInput);
  const downPaymentInput =
    entered !== null && entered >= minDownPayment(wholesale, limits)
      ? payment.downPaymentInput
      : defaultDownPaymentInput(wholesale, limits);

  return { type: 'finance', durationMonths, downPaymentInput };
}
